import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import PackageCard from '../components/PackageCard';
import { Filter, SlidersHorizontal, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { useTheme } from '../contexts/ThemeContext';

const Pacotes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('busca') || '');
  const { themeConfig } = useTheme();
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [cardIndicator, setCardIndicator] = useState('percentage');
  const [daysRange, setDaysRange] = useState([1, 10]);
  const [priceRange, setPriceRange] = useState([0, 15000]);
  const [showFilters, setShowFilters] = useState(false); // Fechado por padrão no mobile
  
  // Ordenação - Alfabética por padrão
  const [sortBy, setSortBy] = useState('alphabetical');

  const categories = [
    { value: 'nacional-aereo', label: 'Aéreo Nacional' },
    { value: 'internacional-aereo', label: 'Aéreo Internacional' },
    { value: 'rodoviario', label: 'Rodoviário' },
    { value: 'passeio-1-dia', label: 'Passeio de 1 Dia' }
  ];

  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  // Load packages and destinations on mount
  useEffect(() => {
    loadPackages();
    loadDestinations();
    // Load card indicator setting
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packages/site-settings`).then(r => r.json()).then(d => {
      if (d?.card_indicator) setCardIndicator(d.card_indicator);
    }).catch(() => {});
  }, []);

  // Check for search query from URL
  useEffect(() => {
    const query = searchParams.get('busca');
    if (query) {
      setSearchQuery(query);
    }
  }, [searchParams]);

  // Apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  }, [selectedCategories, selectedMonths, selectedDestinations, selectedYear, daysRange, priceRange, packages, searchQuery]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const data = await packagesAPI.getAll();
      setPackages(data);
      setFilteredPackages(data);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDestinations = async () => {
    try {
      const data = await packagesAPI.getDestinations();
      setDestinations(data);
    } catch (error) {
      console.error('Error loading destinations:', error);
    }
  };

  const applyFilters = async () => {
    try {
      const filters = {};
      
      // Only add filters that are different from defaults
      if (selectedCategories.length > 0) {
        filters.categories = selectedCategories;
      }
      if (selectedMonths.length > 0) {
        filters.months = selectedMonths;
      }
      if (selectedDestinations.length > 0) {
        filters.destinations = selectedDestinations;
      }
      if (daysRange[0] !== 1) {
        filters.min_days = daysRange[0];
      }
      if (daysRange[1] !== 10) {
        filters.max_days = daysRange[1];
      }
      if (priceRange[0] !== 0) {
        filters.min_price = priceRange[0];
      }
      if (priceRange[1] !== 15000) {
        filters.max_price = priceRange[1];
      }

      let data = await packagesAPI.filter(filters);
      
      // Apply text search filter locally
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        data = data.filter(pkg => 
          pkg.name?.toLowerCase().includes(query) ||
          pkg.short_description?.toLowerCase().includes(query) ||
          pkg.description?.toLowerCase().includes(query) ||
          pkg.departure?.toLowerCase().includes(query)
        );
      }
      
      // Apply year filter (combinado com meses selecionados quando houver)
      // Regra: a data precisa satisfazer ano E mês simultaneamente.
      // Ex.: Janeiro + 2026 não deve mostrar um pacote cuja única data em
      // janeiro é /2027 e cuja única data em 2026 é outubro.
      const hasYear = selectedYear && selectedYear !== 'all';
      const hasMonths = selectedMonths && selectedMonths.length > 0;
      if (hasYear || hasMonths) {
        const yearInt = hasYear ? parseInt(selectedYear) : null;
        data = data.filter(pkg => {
          return pkg.dates?.some(d => {
            if (!d.start_date) return false;
            // Usa split para evitar timezone drift (yyyy-mm-dd)
            const iso = String(d.start_date).split('T')[0];
            const [y, m] = iso.split('-').map(n => parseInt(n, 10));
            if (!y || !m) return false;
            if (hasYear && y !== yearInt) return false;
            if (hasMonths && !selectedMonths.includes(m)) return false;
            return true;
          });
        });
      }
      
      // Filtrar pacotes que ainda têm pelo menos uma data futura (não esconder no backend, apenas no frontend)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      data = data.filter(pkg => {
        if (!pkg.dates || pkg.dates.length === 0) return false;
        return pkg.dates.some(d => {
          if (!d.start_date) return false;
          const startDate = new Date(d.start_date.split('T')[0] + 'T00:00:00');
          return startDate >= today;
        });
      });
      
      // Aplicar ordenação (passa meses selecionados para ordenar corretamente)
      data = sortPackages(data, sortBy, selectedMonths);
      
      setFilteredPackages(data);
    } catch (error) {
      console.error('Error filtering packages:', error);
    }
  };
  
  // Helper: pega a primeira data futura relevante de um pacote
  // Se há meses selecionados, considera apenas datas nesses meses
  const getRelevantDate = (pkg, monthsFilter = []) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const futureDates = (pkg.dates || [])
      .filter(d => {
        if (!d.start_date) return false;
        const sd = new Date(d.start_date.split('T')[0] + 'T00:00:00');
        if (sd < today) return false;
        // Se tem filtro de mês, só considerar datas desse mês
        if (monthsFilter.length > 0) {
          return monthsFilter.includes(sd.getMonth() + 1);
        }
        return true;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    
    return futureDates[0]?.start_date ? new Date(futureDates[0].start_date) : null;
  };

  // Função de ordenação
  const sortPackages = (pkgs, sortType, monthsFilter = []) => {
    const sorted = [...pkgs];
    
    switch (sortType) {
      case 'price-asc':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'price-desc':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'name-asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'name-desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      case 'date-asc':
        return sorted.sort((a, b) => {
          const dateA = getRelevantDate(a, monthsFilter) || new Date('2099-12-31');
          const dateB = getRelevantDate(b, monthsFilter) || new Date('2099-12-31');
          return dateA - dateB;
        });
      case 'date-desc':
        return sorted.sort((a, b) => {
          const dateA = getRelevantDate(a, monthsFilter) || new Date('1900-01-01');
          const dateB = getRelevantDate(b, monthsFilter) || new Date('1900-01-01');
          return dateB - dateA;
        });
      case 'alphabetical':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
      default:
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
    }
  };
  
  // Re-ordenar quando sortBy mudar
  useEffect(() => {
    if (filteredPackages.length > 0) {
      setFilteredPackages(prev => sortPackages([...prev], sortBy, selectedMonths));
    }
  }, [sortBy]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleMonth = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month)
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  };

  const toggleDestination = (destination) => {
    setSelectedDestinations(prev => 
      prev.includes(destination)
        ? prev.filter(d => d !== destination)
        : [...prev, destination]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedMonths([]);
    setSelectedDestinations([]);
    setSelectedYear('all');
    setDaysRange([1, 10]);
    setPriceRange([0, 15000]);
  };

  const hasActiveFilters = selectedCategories.length > 0 || 
                          selectedMonths.length > 0 || 
                          selectedDestinations.length > 0 ||
                          selectedYear !== 'all' ||
                          daysRange[0] !== 1 || daysRange[1] !== 10 ||
                          priceRange[0] !== 0 || priceRange[1] !== 15000;

  // Extrair anos únicos dos pacotes
  const availableYears = [...new Set(
    packages.flatMap(pkg => 
      pkg.dates?.map(d => {
        if (d.start_date) {
          return new Date(d.start_date).getFullYear();
        }
        return null;
      }).filter(Boolean) || []
    )
  )].sort();

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700] mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pacotes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: themeConfig?.theme?.backgroundLight || '#f9fafb' }}>
      {/* Header - Menor no mobile */}
      <div 
        className="text-white py-8 md:py-16"
        style={{ backgroundColor: themeConfig?.theme?.primary || '#003366' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-2 md:mb-4">Nossos Pacotes</h1>
          <p className="text-sm sm:text-base md:text-xl opacity-80">Escolha sua próxima aventura entre nossos destinos incríveis</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-4 lg:mb-6">
                <div className="flex items-center space-x-2">
                  <SlidersHorizontal className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 text-sm text-theme-primary font-medium lg:hidden"
                >
                  {showFilters ? 'Ocultar' : 'Mostrar'}
                  <Filter className="w-4 h-4" />
                </button>
              </div>

              {/* No desktop, sempre mostrar. No mobile, toggle */}
              <div className={`${showFilters ? 'block' : 'hidden'} lg:block`}>
                <div className="space-y-6">
                  {/* Tipo de Pacote */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Tipo de Pacote</h3>
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <div key={cat.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={cat.value}
                            checked={selectedCategories.includes(cat.value)}
                            onCheckedChange={() => toggleCategory(cat.value)}
                          />
                          <Label htmlFor={cat.value} className="text-sm cursor-pointer">
                            {cat.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mês da Viagem */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Mês da Viagem</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {months.map((month) => (
                        <Button
                          key={month.value}
                          onClick={() => toggleMonth(month.value)}
                          variant={selectedMonths.includes(month.value) ? 'default' : 'outline'}
                          size="sm"
                          className={`text-xs ${selectedMonths.includes(month.value) ? 'btn-primary-theme' : ''}`}
                        >
                          {month.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Ano da Viagem */}
                  {availableYears.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3">Ano da Viagem</h3>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => setSelectedYear('all')}
                          variant={selectedYear === 'all' ? 'default' : 'outline'}
                          size="sm"
                          className={`text-xs ${selectedYear === 'all' ? 'btn-primary-theme' : ''}`}
                        >
                          Todos
                        </Button>
                        {availableYears.map((year) => (
                          <Button
                            key={year}
                            onClick={() => setSelectedYear(year.toString())}
                            variant={selectedYear === year.toString() ? 'default' : 'outline'}
                            size="sm"
                            className={`text-xs ${selectedYear === year.toString() ? 'btn-primary-theme' : ''}`}
                          >
                            {year}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quantidade de Dias */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Duração: {daysRange[0]} - {daysRange[1]} dias
                    </h3>
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={daysRange}
                      onValueChange={setDaysRange}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>1 dia</span>
                      <span>10 dias</span>
                    </div>
                  </div>

                  {/* Faixa de Preço */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Preço: R$ {priceRange[0].toLocaleString()} - R$ {priceRange[1].toLocaleString()}
                    </h3>
                    <Slider
                      min={0}
                      max={15000}
                      step={100}
                      value={priceRange}
                      onValueChange={setPriceRange}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>R$ 0</span>
                      <span>R$ 15.000</span>
                    </div>
                  </div>

                  {/* Destinos */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Destinos</h3>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {destinations.map((dest) => (
                        <div key={dest} className="flex items-center space-x-2">
                          <Checkbox
                            id={dest}
                            checked={selectedDestinations.includes(dest)}
                            onCheckedChange={() => toggleDestination(dest)}
                          />
                          <Label htmlFor={dest} className="text-sm cursor-pointer">
                            {dest}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  {hasActiveFilters && (
                    <Button
                      onClick={clearFilters}
                      variant="outline"
                      className="w-full"
                    >
                      Limpar Filtros
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Packages Grid */}
          <div className="flex-1">
            {/* Search bar na página */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar pacotes... (ex: Chapada, Jalapão, Atacama)"
                  className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent text-sm md:text-base"
                />
                {searchQuery ? (
                  <button
                    onClick={clearSearch}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                ) : null}
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Results count and Sort */}
            <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-gray-600 text-sm md:text-base">
                Mostrando <span className="font-semibold">{filteredPackages.length}</span> pacote(s)
                {searchQuery && ` para "${searchQuery}"`}
                {hasActiveFilters && ' com filtros aplicados'}
              </p>
              
              {/* Ordenação */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#F4A831]"
                >
                  <option value="alphabetical">Alfabético (A-Z)</option>
                  <option value="price-asc">Menor preço</option>
                  <option value="price-desc">Maior preço</option>
                  <option value="name-asc">A - Z</option>
                  <option value="name-desc">Z - A</option>
                  <option value="date-asc">Mais próxima</option>
                  <option value="date-desc">Mais distante</option>
                </select>
              </div>
            </div>

            {/* Grid - 2 colunas no mobile */}
            {filteredPackages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
                {filteredPackages.map((pkg) => (
                  <PackageCard key={pkg.id} packageData={pkg} cardIndicator={cardIndicator} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl text-gray-600 mb-2">Nenhum pacote encontrado</p>
                <p className="text-gray-500">Tente ajustar os filtros para ver mais opções</p>
                {hasActiveFilters && (
                  <Button
                    onClick={clearFilters}
                    variant="outline"
                    className="mt-4"
                  >
                    Limpar Filtros
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pacotes;
